import _ from "lodash";
/** External */
import React, { useCallback, useState } from "react";
import { connect } from "react-redux";
import { compose, graphql } from "lib/apolloClient";
import { Modal, Button } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";

/** Internal */
import { FormTextInput } from "UIComponents/InputTextField";
import withLoader from "UIComponents/GqlWithLoader";
import { getOrganizationCurriculumProgramsTypesQuery } from "AddOnManager/modules/AddOnManagerQueries";

/** Relative */
import CurriculumProgramList from "./CurriculumProgramList";
import classes from "./AITutor.scss";

const DebouncedInput = ({ onChange, organizationId, disableOrgIdInput }) => {
  const [input, setInput] = useState(organizationId);
  const { t } = useI18n();

  const handleDebouncedInputChange = useCallback(
    _.debounce(value => {
      onChange(value);
    }, 400),
    []
  );

  const handleInputChange = value => {
    setInput(value);
    handleDebouncedInputChange(value);
  };

  return (
    <FormTextInput
      size="large"
      label={t("common:org_id")}
      error=""
      value={input}
      isRequired={true}
      updateInputField={handleInputChange}
      disabled={disableOrgIdInput}
    />
  );
};

const AITutor = ({
  orgId,
  closeModal,
  onChangeOrganizationId,
  curriculumProgramsOfOrg,
  onSuccessAddOnConfigChange,
}) => {
  const { t } = useI18n();
  const handleOrganizationIdChange = value => {
    onChangeOrganizationId(_.trim(value));
  };

  return (
    <Modal width="800px" height="700px" isOpen onClose={closeModal}>
      <Modal.Header
        title={t("ai:enable_disable_ai_tutor_features")}
        isClosable
      />
      <Modal.Body isScrollable>
        <div className={classes.container}>
          <DebouncedInput
            organizationId={orgId}
            onChange={handleOrganizationIdChange}
            disableOrgIdInput={true}
          />
          <CurriculumProgramList
            orgId={orgId}
            curriculumProgramsOfOrg={curriculumProgramsOfOrg}
            onSuccessAddOnConfigChange={onSuccessAddOnConfigChange}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className={classes.footer}>
          <div className={classes.footerInfoText}>
            {t("ai:curriculum_level_changes_are_updated_in_real_time")}
          </div>
          <div className={classes.rightFooterContainer}>
            <Button
              _legacy_variant="outlined-subtle"
              variant="neutral"
              type="outlined"
              size="large"
              onClick={closeModal}
            >
              {t("common:close")}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { orgId } = ownProps;
  return {
    isData: true,
    isLoading: false,
    organizationId: orgId,
  };
};

export default compose(
  connect(mapStateToProps, null),
  graphql(getOrganizationCurriculumProgramsTypesQuery, {
    name: "getOrganizationCurriculumProgramsTypes",
    options: ({ organizationId, curriculumProgramIds = [] }) => {
      return {
        variables: {
          organizationId,
          curriculumProgramIds,
        },
        fetchPolicy: "cache-and-network",
      };
    },
    props: ({
      getOrganizationCurriculumProgramsTypes,
      ownProps: { isData, isLoading },
    }) => {
      const data = getOrganizationCurriculumProgramsTypes || {};

      const curriculumProgramsOfOrg = _.get(
        data,
        "node.curriculumPrograms",
        []
      );

      return {
        curriculumProgramsOfOrg,
        isData: !_.isEmpty(data) && isData,
        isLoading:
          _.includes(
            [1, 2],
            getOrganizationCurriculumProgramsTypes["networkStatus"]
          ) || isLoading,
      };
    },
  }),
  withLoader
)(AITutor);
